import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EElementSize } from '../../types';
import { DropContent } from '../drop-content';
import { DropDown, DropDownProvider, useDropDownContext } from '../drop-down';
import { DropHeader } from '../drop-header';
import { FieldText } from '../field-text';
import { SearchIcon } from '../icons';
import { LoadingIndicator } from '../loading-indicator';
import { SelectShevronAction } from '../select-shevron-action';
import { SelectListItemStyled, SelectListStyled } from './select.styled';
import { ISelectListProps, ISelectProps } from './select.types';

function SelectList<T>({
  options,
  hasFilter = true,
  value,
  isLoading,
  onChange,
  onFilter,
  selectedPredicate,
}: ISelectListProps<T>) {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState('');
  const { closeDropdown } = useDropDownContext();

  const filterHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      setSearchText(e.target.value);

      if (typeof onFilter === 'function') {
        onFilter(e.target.value);
      }
    },
    [onFilter],
  );

  const filteredList = useMemo(
    () =>
      (searchText && typeof onFilter !== 'function'
        ? options.filter(el =>
            el.caption
              .toLocaleLowerCase()
              .includes(searchText.toLocaleLowerCase()),
          )
        : options
      ).map(el => ({ ...el })),
    [onFilter, options, searchText],
  );

  const itemClickHandler = useCallback(
    (item: T) => {
      if (!onChange) {
        return;
      }

      onChange(item);
      closeDropdown();
    },
    [closeDropdown, onChange],
  );

  return (
    <DropContent>
      {hasFilter && (
        <DropHeader $size={EElementSize.S}>
          <FieldText
            value={searchText}
            onChange={filterHandler}
            $size={EElementSize.S}
            icon={<SearchIcon />}
            placeholder={t('search')}
            autoFocus
          />
        </DropHeader>
      )}
      <SelectListStyled>
        {isLoading ? (
          <LoadingIndicator type="full" />
        ) : (
          filteredList.map(el => (
            <SelectListItemStyled
              $isDisabled={el.disabled}
              key={el.key || String(el.value)}
              $isSelected={
                selectedPredicate
                  ? selectedPredicate(el, value)
                  : el.value === value
              }
              onClick={() => !el.disabled && itemClickHandler(el.value)}
            >
              {el.caption}
            </SelectListItemStyled>
          ))
        )}
      </SelectListStyled>
    </DropContent>
  );
}

export function Select<T>({
  options,
  value,
  placeholder,
  onChange,
  onClear,
  width,
  isClearable,
  isSearchable,
  isLoading,
  isListLoading,
  disabled,
  onFilter,
  $size = EElementSize.M,
  selectedPredicate,
}: ISelectProps<T>) {
  const selectedItem = useMemo(() => {
    return options.find(el => {
      if (typeof selectedPredicate === 'function') {
        return selectedPredicate(el, value);
      } else {
        return el.value === value;
      }
    });
  }, [options, selectedPredicate, value]);

  const formattedSelectedItem = useMemo(
    () => String(selectedItem?.caption ?? ''),
    [selectedItem?.caption],
  );

  return (
    <DropDownProvider>
      <DropDown
        content={
          <SelectList<T>
            hasFilter={isSearchable}
            options={options}
            value={value}
            onChange={onChange}
            selectedPredicate={selectedPredicate}
            onFilter={onFilter}
            isLoading={isListLoading}
          />
        }
        disabled={disabled || isLoading}
        offset={4}
        placement="bottom-start"
      >
        {isLoading ? (
          <LoadingIndicator $small={true} />
        ) : (
          <FieldText
            $size={$size}
            actionButtons={<SelectShevronAction />}
            value={formattedSelectedItem}
            isClearable={!!formattedSelectedItem && isClearable}
            onClear={onClear}
            disabled={disabled}
            width={width}
            cursor="pointer"
            placeholder={placeholder}
            readOnly
          />
        )}
      </DropDown>
    </DropDownProvider>
  );
}
